/** Model/route capability helpers for Canvas image reference limits.
 *
 * Driven by `GET .../image-generation/config` provider_channels — never
 * hardcodes per-provider limits in UI components. */

import type { TFunction } from 'i18next'
import type { ImageGenerationConfig, ImageGenerationProviderChannel } from '../../../api'
import { localizeImageGenerationPromptLimitError } from './imageGenerationPromptCapability'
import { isImageReferenceAssetHandle, nodeOutputDataTypes, type InputMaterial } from './dataflow'
import {
  PROVIDER_CHANNEL_LEGACY,
  parseProviderChannels,
} from './imageGenerationProvider'

export interface ImageGenerationConfigImageToImage {
  operation?: string
  required_input_role?: string
  required_input_count?: number
  max_reference_images?: number
  supports_multi_reference?: boolean
  reference_limit_label?: string
}

export interface ImageGenerationConfigShape {
  image_to_image?: ImageGenerationConfigImageToImage
  provider_channels?: ImageGenerationProviderChannel[]
}

export type { ImageGenerationConfig }

export interface ImageReferenceCapability {
  maxReferenceImages: number
  supportsMultiReference: boolean
  requiredInputRole: string
  referenceLimitLabel: string
  providerChannel: string
}

export interface ImageReferenceLimitState {
  imageCount: number
  maxReferenceImages: number
  exceedsLimit: boolean
  excessCount: number
}

/** VistoeraTASK-00574 — hard product ceiling for Generate Image reference
 * inputs across EVERY entry path (picker / manual wire / drag). Provider
 * channels may advertise a lower cap; they must never exceed this. */
export const GENERATE_IMAGE_MAX_REFERENCE_IMAGES = 9

function clampGenerateImageReferenceMax(max: number): number {
  return Math.min(Math.max(0, max), GENERATE_IMAGE_MAX_REFERENCE_IMAGES)
}

const DEFAULT_CAPABILITY: ImageReferenceCapability = {
  maxReferenceImages: 1,
  supportsMultiReference: false,
  requiredInputRole: 'reference_image',
  referenceLimitLabel: '',
  providerChannel: PROVIDER_CHANNEL_LEGACY,
}

export function capabilityForProviderChannel(
  providerChannel: string | undefined,
  channels: ImageGenerationProviderChannel[],
): ImageReferenceCapability {
  if (!providerChannel || providerChannel === PROVIDER_CHANNEL_LEGACY) {
    return DEFAULT_CAPABILITY
  }
  const channel = channels.find((c) => c.id === providerChannel)
  if (!channel) return DEFAULT_CAPABILITY
  const max = clampGenerateImageReferenceMax(channel.max_reference_images)
  return {
    maxReferenceImages: max,
    supportsMultiReference: channel.supports_multi_reference && max > 1,
    requiredInputRole: 'reference_image',
    referenceLimitLabel: channel.label,
    providerChannel: channel.id,
  }
}

export function parseImageReferenceCapability(
  config: ImageGenerationConfigShape | null | undefined,
  providerChannel?: string,
): ImageReferenceCapability {
  const channels = parseProviderChannels(config)
  if (providerChannel) return capabilityForProviderChannel(providerChannel, channels)
  const i2i = config?.image_to_image
  if (!i2i) return DEFAULT_CAPABILITY
  const raw =
    typeof i2i.max_reference_images === 'number'
      ? i2i.max_reference_images
      : typeof i2i.required_input_count === 'number'
        ? i2i.required_input_count
        : DEFAULT_CAPABILITY.maxReferenceImages
  const max = clampGenerateImageReferenceMax(raw)
  return {
    maxReferenceImages: max,
    supportsMultiReference: (i2i.supports_multi_reference ?? raw > 1) && max > 1,
    requiredInputRole: i2i.required_input_role || DEFAULT_CAPABILITY.requiredInputRole,
    referenceLimitLabel: i2i.reference_limit_label || DEFAULT_CAPABILITY.referenceLimitLabel,
    providerChannel: PROVIDER_CHANNEL_LEGACY,
  }
}

export function countImageReferenceMaterials(materials: InputMaterial[]): number {
  return materials.filter((m) => m.kind === 'IMAGE').length
}

export function evaluateImageReferenceLimit(
  imageCount: number,
  capability: ImageReferenceCapability,
): ImageReferenceLimitState {
  const max = capability.maxReferenceImages
  const exceedsLimit = imageCount > max
  return {
    imageCount,
    maxReferenceImages: max,
    exceedsLimit,
    excessCount: exceedsLimit ? imageCount - max : 0,
  }
}

function resolveReferenceLimitLabel(capability: ImageReferenceCapability, t: TFunction<'canvas'>): string {
  return capability.referenceLimitLabel || t('generation.referenceLimitDefaultLabel')
}

export function referenceLimitConnectMessage(capability: ImageReferenceCapability, t: TFunction<'canvas'>): string {
  if (capability.maxReferenceImages === 1) {
    return t('generation.referenceLimitConnectSingle', { label: resolveReferenceLimitLabel(capability, t) })
  }
  return t('generation.referenceLimitConnectMulti', { count: capability.maxReferenceImages })
}

/** VistoeraTASK-00574 — Related Assets 「+ 添加」 overflow copy.
 * Uses the product hard-ceiling wording when the effective max is the
 * 9-image product ceiling; otherwise falls back to the provider-aware
 * connect message (e.g. single-reference channels). */
export function relatedAssetsAddLimitMessage(capability: ImageReferenceCapability, t: TFunction<'canvas'>): string {
  if (capability.maxReferenceImages >= GENERATE_IMAGE_MAX_REFERENCE_IMAGES) {
    return t('generation.relatedAssets.maxNine')
  }
  return referenceLimitConnectMessage(capability, t)
}

/** Remaining slots a Generate Image node can still accept (product +
 * provider ceiling already applied via ``capability.maxReferenceImages``). */
export function remainingGenerateImageReferenceSlots(
  imageCount: number,
  capability: ImageReferenceCapability,
): number {
  return Math.max(0, capability.maxReferenceImages - imageCount)
}

/** VistoeraTASK-00136 — never hardcode a specific Provider's name here:
 * this message is shown for ANY currently-selected provider channel
 * whose OWN capability caps it at 1 reference image (today that is
 * "本地 · FLUX" by product policy, but the capability comes from
 * `capability.maxReferenceImages`/`capability.referenceLimitLabel` —
 * the SAME canonical, currently-selected-provider-derived fields the
 * dropdown/capability resolver already use — never a separate,
 * provider-name-baked-in string that can drift out of sync with
 * whichever provider actually produced this `capability`). */
export function referenceLimitValidationMessage(capability: ImageReferenceCapability, t: TFunction<'canvas'>): string {
  if (capability.maxReferenceImages === 1) {
    return t('generation.referenceLimitValidationSingle', { label: resolveReferenceLimitLabel(capability, t) })
  }
  return t('generation.referenceLimitValidationMulti', { count: capability.maxReferenceImages })
}

const REFERENCE_LIMIT_ERROR_PATTERNS = [
  /IMAGE_TO_IMAGE requires exactly one/i,
  /exactly one reference_image/i,
  /exactly one input asset/i,
  /reference count exceeds provider capability/i,
]

/** Map raw Backend technical errors to tenant-safe localized copy. */
export function localizeImageGenerationSubmitError(
  message: string | null | undefined,
  t: TFunction<'canvas'>,
): string {
  const normalized = (message || '').trim()
  if (!normalized) return t('generation.errorSubmitFailed')
  if (REFERENCE_LIMIT_ERROR_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return t('generation.errorReferenceLimitExceeded')
  }
  const promptLimitMessage = localizeImageGenerationPromptLimitError(normalized, t)
  if (promptLimitMessage) return promptLimitMessage
  if (normalized === 'PROMPT_EXCEEDS_PROVIDER_LIMIT' || /提示词超过当前模型限制/.test(normalized)) {
    return t('generation.promptLimitValidationGeneric')
  }
  return normalized
}

export function sourceNodeProvidesImage(sourceNodeType: string | undefined): boolean {
  if (!sourceNodeType) return false
  return nodeOutputDataTypes(sourceNodeType).includes('IMAGE')
}

interface GraphEdgeLike {
  source: string
  target: string
  targetHandle?: string | null
}

interface GraphNodeLike {
  id: string
  data: { nodeType?: string; fields?: Record<string, unknown> }
}

export function countImageReferenceEdgesForTarget(
  edges: GraphEdgeLike[],
  nodes: GraphNodeLike[],
  targetId: string,
): number {
  let count = 0
  for (const edge of edges) {
    if (edge.target !== targetId) continue
    const handle = edge.targetHandle ?? null
    // VistoeraTASK-00144 — count bare AND per-edge-scoped reference
    // handles alike; a strict equality check here would silently stop
    // enforcing the reference-image limit the instant edges got scoped.
    if (handle !== null && !isImageReferenceAssetHandle(handle)) continue
    const sourceNode = nodes.find((n) => n.id === edge.source)
    if (!sourceNode) continue
    if (sourceNodeProvidesImage(sourceNode.data.nodeType)) count += 1
  }
  return count
}

export function wouldExceedImageReferenceLimitOnConnect(args: {
  edges: GraphEdgeLike[]
  nodes: GraphNodeLike[]
  connection: { source?: string | null; target?: string | null; targetHandle?: string | null }
  config?: ImageGenerationConfigShape | ImageGenerationConfig | null
  capability?: ImageReferenceCapability
  workspaceId?: string
}): boolean {
  const { edges, nodes, connection, config, capability, workspaceId } = args
  const targetId = connection.target
  const sourceId = connection.source
  if (!targetId || !sourceId) return false

  const targetNode = nodes.find((n) => n.id === targetId)
  if (targetNode?.data.nodeType !== 'generate_image') return false

  const handle = connection.targetHandle ?? null
  if (handle !== null && !isImageReferenceAssetHandle(handle)) return false

  const sourceNode = nodes.find((n) => n.id === sourceId)
  if (!sourceNodeProvidesImage(sourceNode?.data.nodeType)) return false

  const targetFields = (targetNode.data as { fields?: Record<string, unknown> }).fields || {}
  const resolvedCapability =
    capability ||
    resolveCapabilityForGenerateImageNode({
      config: config || null,
      fields: targetFields,
      workspaceId,
    })

  const currentCount = countImageReferenceEdgesForTarget(edges, nodes, targetId)
  return currentCount >= resolvedCapability.maxReferenceImages
}

export function resolveCapabilityForGenerateImageNode(args: {
  config: ImageGenerationConfigShape | ImageGenerationConfig | null | undefined
  fields: Record<string, unknown>
  workspaceId?: string
}): ImageReferenceCapability {
  // VistoeraTASK-00495 — the backend `provider_channels` config is the ONLY source of the
  // reference cap.  No workspace-id / environment special case: the former hard-coded
  // Preview-workspace override made Preview multi-reference un-promotable to Production.
  const providerChannel = args.fields.providerChannel as string | undefined
  return parseImageReferenceCapability(args.config, providerChannel)
}
